import { useState, useLayoutEffect, useMemo, useCallback } from "react";
import { SectionList, Pressable } from "react-native";
import { useRouter, useNavigation } from "expo-router";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useQueryClient } from "@tanstack/react-query";
import { View, Text, ScreenContainer, FormInput } from "@src/components";
import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";
import {
  useTenant,
  useAppDirectoryTenants,
  getTenantDisplayName,
  filterTenants,
  type Tenant,
} from "@src/features/tenants";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { Icon } from "@src/components/Icon";
import { Loading } from "@src/components/Loading";
import { DataStateView } from "@src/components/screen-state";

/** Row wrapper so the same tenant can appear in two sections with stable list keys */
type ListedTenant = { rowKey: string; tenant: Tenant };

const keyExtractor = (item: ListedTenant) => item.rowKey;

type TenantSection = {
  title: string;
  data: readonly ListedTenant[];
  /** Extra space above header when this section follows Selected Organisation */
  spacingAfterSelected?: boolean;
};

export default function TenantSelectScreen() {
  const router = useRouter();
  const navigation = useNavigation();
  const { headerLeft } = useHeaderBackButton();
  const theme = UnistylesRuntime.getTheme();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  const queryClient = useQueryClient();
  const { data: tenantsPayload, isFetching: isTenantsQueryFetching } = useAppDirectoryTenants();
  const { selectedTenant, selectTenant, isLoading, error, refreshTenants } = useTenant();

  const [searchQuery, setSearchQuery] = useState("");
  const [isSwitching, setIsSwitching] = useState(false);

  const memoizedContentContainerStyle = useMemo(
    () => [
      styles.container,
      {
        paddingBottom: safeAreaBottom + theme.spacing["5xl"],
      },
    ],
    [safeAreaBottom, theme],
  );

  const handleSelectTenant = useCallback(
    async (tenantId: string) => {
      if (tenantId === selectedTenant?.id) {
        return;
      }
      setIsSwitching(true);
      await selectTenant(tenantId);
      queryClient.invalidateQueries({ queryKey: ["cameras"], exact: false });
      router.dismissTo("/(tabs)/overview");
    },
    [selectedTenant?.id, selectTenant, queryClient, router],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Select Organisation",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  const renderItem = useCallback(
    ({ item: { tenant }, section }: { item: ListedTenant; section: TenantSection }) => {
      const isSelected = selectedTenant?.id === tenant.id;
      const displayName = getTenantDisplayName(tenant);
      const isSelectedOrganisationSection = section.title === "Selected Organisation";
      return (
        <Pressable
          disabled={isSelected}
          onPress={() => handleSelectTenant(tenant.id)}
          style={[styles.item, isSelectedOrganisationSection && styles.itemWithoutDivider]}
        >
          <View style={styles.itemContent}>
            <Text textStyle="md/medium">{displayName}</Text>
            <Text textStyle="xs/regular" color="textMuted">
              {tenant.id}
            </Text>
          </View>
          {isSelected && (
            <Icon
              name="checkmark"
              size={theme.spacing["6xl"]}
              color={theme.colors.interactive.buttons.default_background}
            />
          )}
        </Pressable>
      );
    },
    [selectedTenant?.id, handleSelectTenant, theme],
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: TenantSection }) => (
      <View style={[styles.sectionHeader, section.spacingAfterSelected && styles.sectionHeaderAfterSelected]}>
        <Text textStyle="xs/medium" style={{ color: theme.colors.interactive.controls.default_midground }}>
          {section.title}
        </Text>
      </View>
    ),
    [theme.colors.interactive.controls.default_midground],
  );

  const listEmptyForSearch = useCallback(() => {
    return (
      <View py="4xl" alignItems="center">
        <Text color="textMuted" textStyle="sm/regular">
          No organisations found
        </Text>
      </View>
    );
  }, []);

  if (isSwitching) {
    return (
      <ScreenContainer edges={["bottom"]}>
        <View flex={1} alignItems="center" justifyContent="center" gap="lg">
          <Loading size="large" color={theme.colors.textMuted} />
          <Text textStyle="md/regular" color="textMuted">
            Switching organisation...
          </Text>
        </View>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={tenantsPayload?.tenants}
        isLoading={isLoading}
        isFetching={isTenantsQueryFetching}
        error={error}
        refetch={() => void refreshTenants()}
        isEmpty={(list) => list.length === 0}
        loadingMessage="Loading organisations..."
        errorTitle="Unable to load organisations"
        errorMessage="Please check your connection and try again."
        emptyState={{
          icon: "building",
          title: "No organisations found",
          message: "You do not have access to any organisations yet.",
          action: {
            label: "Refresh",
            onPress: () => void refreshTenants(),
          },
        }}
      >
        {(tenantList) => {
          const filtered = filterTenants(tenantList, searchQuery);
          const selectedRow = selectedTenant ? tenantList.find((t) => t.id === selectedTenant.id) : undefined;
          const allListed = filtered.map((t) => ({ rowKey: `all-${t.id}`, tenant: t }) satisfies ListedTenant);
          const sections: TenantSection[] =
            selectedRow ?
              [
                {
                  title: "Selected Organisation",
                  data: [{ rowKey: `selected-${selectedRow.id}`, tenant: selectedRow }],
                },
                { title: "All Organisations", data: allListed, spacingAfterSelected: true },
              ]
            : [{ title: "All Organisations", data: allListed }];

          return (
            <View flex={1}>
              <View px="5xl" py="4xl" bg="background">
                <FormInput
                  placeholder="Search organisations"
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  size="medium"
                  clearable
                />
              </View>

              <SectionList
                sections={sections}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                renderSectionHeader={renderSectionHeader}
                contentContainerStyle={memoizedContentContainerStyle}
                keyboardShouldPersistTaps="handled"
                keyboardDismissMode="on-drag"
                ListEmptyComponent={listEmptyForSearch}
                stickySectionHeadersEnabled={false}
                windowSize={150}
                maxToRenderPerBatch={150}
                initialNumToRender={20}
              />
            </View>
          );
        }}
      </DataStateView>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
    flexGrow: 1,
  },
  sectionHeader: {
    paddingHorizontal: theme.spacing.xl,
    paddingTop: theme.spacing["3xl"],
    paddingBottom: theme.spacing.md,
  },
  sectionHeaderAfterSelected: {
    paddingTop: theme.spacing["10xl"],
  },
  item: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: theme.spacing["5xl"],
    paddingHorizontal: theme.spacing.xl,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  itemWithoutDivider: {
    borderBottomWidth: 0,
  },
  itemContent: {
    flex: 1,
  },
}));
